import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { withTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Alert,
  CircularProgress,
  Snackbar,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Warehouse as WarehouseIcon,
  PlaylistAdd as PlaylistAddIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useUserStore } from 'store';
import { getCsrfToken } from '../../utils';
import {
  useListPreferences,
  FilterButton,
  FilterPanel,
  FilterSection,
  FilterChip,
  SearchInput,
  FilterSummary,
} from '../shared/ListFilters';
import { usePaginatedList } from '../../hooks/usePaginatedList';
import PaginationControls from '../shared/PaginationControls';
import DataTable from '../shared/DataTable';

const STORAGE_KEY = 'storage_locations_list_preferences';

const SIZE_CATEGORIES = ['small', 'standard', 'large', 'xl'];

const defaultPreferences = {
  sortField: 'building',
  sortDirection: 'asc',
  filters: {
    size_category: '',
    is_occupied: '',
  },
  searchQuery: '',
};

// ==================== DIALOGS ====================

// Mirrors the backend's auto-generated label (build_location_label)
const buildAutoLabel = (values) =>
  [values.building, values.rack, values.shelf, values.position].filter(Boolean).join('-');

// Backend returns machine-readable error codes; map them to translated messages.
// Never show raw server error text to the user.
const getApiErrorMessage = (err, t, fallbackKey) => {
  const data = err?.response?.data;
  const code = Array.isArray(data?.error) ? data.error[0] : data?.error;
  if (code === 'duplicate_location') {
    return t('tire_hotel.duplicate_location');
  }
  if (code === 'location_occupied') {
    return t('tire_hotel.cannot_delete_occupied');
  }
  return t(fallbackKey);
};

function StorageLocationDialog({ open, onClose, onSave, location, t }) {
  const [form, setForm] = useState({
    building: '',
    rack: '',
    shelf: '',
    position: '',
    size_category: 'standard',
    label: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // When the label is auto-generated (not customized by the user), keep it in sync with the
  // coordinate fields and let the backend regenerate it on save.
  const [labelIsAuto, setLabelIsAuto] = useState(true);

  useEffect(() => {
    if (open) {
      setError(null);
      if (location) {
        setForm({
          building: location.building || '',
          rack: location.rack || '',
          shelf: location.shelf || '',
          position: location.position || '',
          size_category: location.size_category || 'standard',
          label: location.label || '',
        });
        setLabelIsAuto(!location.label || location.label === buildAutoLabel(location));
      } else {
        setForm({
          building: '',
          rack: '',
          shelf: '',
          position: '',
          size_category: 'standard',
          label: '',
        });
        setLabelIsAuto(true);
      }
    }
  }, [open, location]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    if (field === 'label') {
      setLabelIsAuto(false);
      setForm((prev) => ({ ...prev, label: value }));
      return;
    }
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (labelIsAuto) {
        next.label = buildAutoLabel(next);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      // Blank label = backend regenerates it from the coordinate fields
      const payload = { ...form, label: labelIsAuto ? '' : form.label };
      await onSave(payload, location?.id);
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err, t, 'tire_hotel.location_save_failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {location ? t('tire_hotel.edit_location') : t('tire_hotel.add_location')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label={t('tire_hotel.building')}
            value={form.building}
            onChange={handleChange('building')}
            size="small"
            fullWidth
          />
          <TextField
            label={t('tire_hotel.rack')}
            value={form.rack}
            onChange={handleChange('rack')}
            size="small"
            fullWidth
            required
          />
          <TextField
            label={t('tire_hotel.shelf')}
            value={form.shelf}
            onChange={handleChange('shelf')}
            size="small"
            fullWidth
          />
          <TextField
            label={t('tire_hotel.position')}
            value={form.position}
            onChange={handleChange('position')}
            size="small"
            fullWidth
          />
          <FormControl size="small" fullWidth>
            <InputLabel>{t('tire_hotel.size_category')}</InputLabel>
            <Select
              value={form.size_category}
              onChange={handleChange('size_category')}
              label={t('tire_hotel.size_category')}
            >
              {SIZE_CATEGORIES.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {t(`tire_hotel.${cat}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={t('tire_hotel.label')}
            value={form.label}
            onChange={handleChange('label')}
            size="small"
            fullWidth
            helperText={t('tire_hotel.label_auto_help')}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('Cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || !form.rack}
        >
          {saving ? <CircularProgress size={20} /> : t('Save Changes')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function BulkAddLocationsDialog({ open, onClose, onSave, t }) {
  const [form, setForm] = useState({
    building: '',
    rack: '',
    shelf: '',
    position_from: 1,
    position_to: 10,
    size_category: 'standard',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setError(null);
      setForm({
        building: '',
        rack: '',
        shelf: '',
        position_from: 1,
        position_to: 10,
        size_category: 'standard',
      });
    }
  }, [open]);

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'number' ? parseInt(e.target.value, 10) || 0 : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err, t, 'tire_hotel.location_save_failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('tire_hotel.bulk_add_title')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label={t('tire_hotel.building')}
            value={form.building}
            onChange={handleChange('building')}
            size="small"
            fullWidth
          />
          <TextField
            label={t('tire_hotel.rack')}
            value={form.rack}
            onChange={handleChange('rack')}
            size="small"
            fullWidth
            required
          />
          <TextField
            label={t('tire_hotel.shelf')}
            value={form.shelf}
            onChange={handleChange('shelf')}
            size="small"
            fullWidth
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label={t('tire_hotel.bulk_add_from')}
              type="number"
              value={form.position_from}
              onChange={handleChange('position_from')}
              size="small"
              fullWidth
              inputProps={{ min: 1 }}
            />
            <TextField
              label={t('tire_hotel.bulk_add_to')}
              type="number"
              value={form.position_to}
              onChange={handleChange('position_to')}
              size="small"
              fullWidth
              inputProps={{ min: 1 }}
            />
          </Box>
          <FormControl size="small" fullWidth>
            <InputLabel>{t('tire_hotel.size_category')}</InputLabel>
            <Select
              value={form.size_category}
              onChange={handleChange('size_category')}
              label={t('tire_hotel.size_category')}
            >
              {SIZE_CATEGORIES.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {t(`tire_hotel.${cat}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('Cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || !form.rack || form.position_from > form.position_to}
        >
          {saving ? <CircularProgress size={20} /> : t('Add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ==================== MAIN PAGE ====================

function StorageLocationsPage({ t }) {
  const { currentOrganization } = useUserStore();

  // Context menu state
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  // Dialog states
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [bulkAddDialogOpen, setBulkAddDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [successSnackbar, setSuccessSnackbar] = useState(null);
  const [actionError, setActionError] = useState(null);

  // Capacity stats
  const [capacity, setCapacity] = useState({
    total: 0,
    occupied: 0,
    available: 0,
  });

  const {
    preferences,
    updatePreferences,
    updateFilters: updateLocalFilters,
    resetFilters: resetLocalFilters,
    handleSort: handleLocalSort,
    filtersOpen,
    toggleFiltersOpen,
  } = useListPreferences(STORAGE_KEY, defaultPreferences);

  // Build server-side filter params from local preferences
  const serverFilters = useMemo(() => {
    const filters = {};
    if (preferences.filters.size_category) {
      filters.size_category = preferences.filters.size_category;
    }
    if (preferences.filters.is_occupied !== '') {
      filters.is_occupied = preferences.filters.is_occupied;
    }
    return filters;
  }, [preferences.filters]);

  const {
    data: items,
    loading,
    error,
    page,
    pageSize,
    totalCount,
    totalPages,
    setPage,
    setPageSize,
    searchQuery,
    setSearchQuery,
    setFilters: setServerFilters,
    setSort,
    refetch,
  } = usePaginatedList('/api/storage-locations/', {
    initialFilters: serverFilters,
    defaultSortField: preferences.sortField,
    defaultSortDirection: preferences.sortDirection,
    initialSearchQuery: preferences.searchQuery || '',
  });

  // Sync local preferences to server filters
  useEffect(() => {
    setServerFilters(serverFilters);
  }, [serverFilters, setServerFilters]);

  // Sync sort from local preferences to server
  useEffect(() => {
    setSort(preferences.sortField, preferences.sortDirection);
  }, [preferences.sortField, preferences.sortDirection, setSort]);

  // Sync search query from preferences to hook
  useEffect(() => {
    setSearchQuery(preferences.searchQuery);
  }, [preferences.searchQuery, setSearchQuery]);

  const handleSort = useCallback((field) => {
    handleLocalSort(field);
  }, [handleLocalSort]);

  const updateFilters = useCallback((newFilters) => {
    updateLocalFilters(newFilters);
  }, [updateLocalFilters]);

  const resetFilters = useCallback(() => {
    resetLocalFilters();
  }, [resetLocalFilters]);

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (preferences.filters.size_category) count++;
    if (preferences.filters.is_occupied !== '') count++;
    return count;
  }, [preferences.filters]);

  // Build filter summary chips
  const filterSummaryChips = useMemo(() => {
    const chips = [];
    if (preferences.filters.size_category) {
      chips.push({
        key: 'size_category',
        label: t(`tire_hotel.${preferences.filters.size_category}`),
      });
    }
    if (preferences.filters.is_occupied === 'true') {
      chips.push({ key: 'occupied', label: t('tire_hotel.occupied') });
    } else if (preferences.filters.is_occupied === 'false') {
      chips.push({ key: 'available', label: t('tire_hotel.available') });
    }
    return chips;
  }, [preferences.filters, t]);

  // Fetch capacity stats
  const fetchCapacity = useCallback(async () => {
    if (!currentOrganization?.organization_id) return;
    try {
      const response = await axios.get('/api/tire-hotel-cards/capacity/', {
        headers: { 'X-Organization-Id': currentOrganization.organization_id },
      });
      setCapacity({
        total: response.data.total || 0,
        occupied: response.data.occupied || 0,
        available: response.data.available || 0,
        capacityAlert: response.data.capacity_alert || false,
        occupancyPercentage: response.data.occupancy_percentage || 0,
        alertThreshold: response.data.alert_threshold || 90,
      });
    } catch (err) {
      console.error('Failed to fetch capacity:', err);
    }
  }, [currentOrganization?.organization_id]);

  useEffect(() => {
    fetchCapacity();
  }, [fetchCapacity]);

  const handleContextMenu = (event, item) => {
    event.stopPropagation();
    setSelectedItem(item);
    setContextMenu({ mouseX: event.clientX, mouseY: event.clientY });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  // Save single location (create or update)
  const handleSaveLocation = async (formData, locationId) => {
    const headers = {
      'X-CSRFToken': getCsrfToken(),
      'X-Organization-Id': currentOrganization.organization_id,
    };
    if (locationId) {
      await axios.patch(`/api/storage-locations/${locationId}/`, formData, { headers });
    } else {
      await axios.post('/api/storage-locations/', formData, { headers });
    }
    setSuccessSnackbar(t('tire_hotel.location_saved'));
    refetch();
    fetchCapacity();
  };

  // Bulk create locations
  const handleBulkAdd = async (formData) => {
    const headers = {
      'X-CSRFToken': getCsrfToken(),
      'X-Organization-Id': currentOrganization.organization_id,
    };
    const response = await axios.post('/api/storage-locations/bulk_create/', formData, { headers });
    const count = response.data?.created?.length || 0;
    const skipped = response.data?.skipped || 0;
    let message = t('tire_hotel.bulk_add_created', { count });
    if (skipped > 0) {
      message += ` (${t('tire_hotel.bulk_add_skipped', { count: skipped })})`;
    }
    setSuccessSnackbar(message);
    refetch();
    fetchCapacity();
  };

  // Delete location
  const handleDeleteLocation = async () => {
    if (!selectedItem) return;
    try {
      const headers = {
        'X-CSRFToken': getCsrfToken(),
        'X-Organization-Id': currentOrganization.organization_id,
      };
      await axios.delete(`/api/storage-locations/${selectedItem.id}/`, { headers });
      setSuccessSnackbar(t('tire_hotel.location_deleted'));
      refetch();
      fetchCapacity();
    } catch (err) {
      setActionError(getApiErrorMessage(err, t, 'tire_hotel.location_delete_failed'));
    }
    setDeleteConfirmOpen(false);
    setSelectedItem(null);
  };

  // Error snackbar
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const prevError = useRef(error);

  useEffect(() => {
    if (error && error !== prevError.current) {
      setShowErrorSnackbar(true);
    }
    prevError.current = error;
  }, [error]);

  const getSizeCategoryColor = (cat) => {
    switch (cat) {
      case 'small': return 'info';
      case 'standard': return 'default';
      case 'large': return 'warning';
      case 'xl': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box data-testid="storage-locations-page">
      {/* Error Snackbar */}
      <Snackbar
        open={showErrorSnackbar && !!error}
        autoHideDuration={6000}
        onClose={() => setShowErrorSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowErrorSnackbar(false)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successSnackbar}
        autoHideDuration={4000}
        onClose={() => setSuccessSnackbar(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessSnackbar(null)} severity="success" sx={{ width: '100%' }}>
          {successSnackbar}
        </Alert>
      </Snackbar>

      {/* Action Error Snackbar (failed delete etc.) */}
      <Snackbar
        open={!!actionError}
        autoHideDuration={6000}
        onClose={() => setActionError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setActionError(null)} severity="error" sx={{ width: '100%' }}>
          {actionError}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            component="h4"
            variant="h4"
            data-testid="storage-locations-heading"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            {t('tire_hotel.storage_locations')}
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              color: 'text.secondary',
              mt: 0.5,
              display: { xs: 'none', sm: 'block' },
            }}
          >
            {t('tire_hotel.locations_subtitle')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PlaylistAddIcon />}
            onClick={() => setBulkAddDialogOpen(true)}
            size="small"
            data-testid="bulk-add-locations-button"
            sx={{
              borderRadius: '10px',
              borderColor: 'divider',
              fontWeight: 500,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            {t('tire_hotel.bulk_add_locations')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingLocation(null);
              setLocationDialogOpen(true);
            }}
            size="small"
            data-testid="add-location-button"
            sx={{
              borderRadius: '10px',
              fontWeight: 500,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            {t('tire_hotel.add_location')}
          </Button>
        </Box>
      </Box>

      {/* Capacity Alert Banner */}
      {capacity.capacityAlert && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('tire_hotel.capacity_alert_message', {
            percentage: capacity.occupancyPercentage,
            threshold: capacity.alertThreshold,
          })}
        </Alert>
      )}

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Card sx={{ minWidth: 150, flex: 1 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="body2" color="text.secondary">
              {t('tire_hotel.total_locations')}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {capacity.total}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 150, flex: 1 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="body2" color="text.secondary">
              {t('tire_hotel.occupied_locations')}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'warning.dark' }}>
              {capacity.occupied}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 150, flex: 1 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="body2" color="text.secondary">
              {t('tire_hotel.available_locations')}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'success.dark' }}>
              {capacity.available}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Search and Filter Row */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 2.5,
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        <SearchInput
          value={preferences.searchQuery}
          onChange={(value) => updatePreferences({ searchQuery: value })}
          onClear={() => updatePreferences({ searchQuery: '' })}
          placeholder={t('tire_hotel.locations_search_placeholder')}
          data-testid="storage-locations-search"
        />
        <FilterButton
          t={t}
          activeCount={activeFilterCount}
          isOpen={filtersOpen}
          onClick={toggleFiltersOpen}
        />
      </Box>

      {/* Active Filter Summary */}
      <FilterSummary chips={filterSummaryChips} onClear={resetFilters} t={t} />

      {/* Filter Panel */}
      <FilterPanel
        open={filtersOpen}
        resultCount={totalCount}
        onReset={resetFilters}
        resetDisabled={activeFilterCount === 0}
        t={t}
      >
        <FilterSection title={t('tire_hotel.size_category')}>
          {SIZE_CATEGORIES.map((cat) => (
            <FilterChip
              key={cat}
              label={t(`tire_hotel.${cat}`)}
              active={preferences.filters.size_category === cat}
              onClick={() =>
                updateFilters({
                  size_category: preferences.filters.size_category === cat ? '' : cat,
                })
              }
              color={getSizeCategoryColor(cat)}
            />
          ))}
        </FilterSection>
        <FilterSection title={t('tire_hotel.status')}>
          <FilterChip
            label={t('tire_hotel.occupied')}
            active={preferences.filters.is_occupied === 'true'}
            onClick={() =>
              updateFilters({
                is_occupied: preferences.filters.is_occupied === 'true' ? '' : 'true',
              })
            }
            color="warning"
          />
          <FilterChip
            label={t('tire_hotel.available')}
            active={preferences.filters.is_occupied === 'false'}
            onClick={() =>
              updateFilters({
                is_occupied: preferences.filters.is_occupied === 'false' ? '' : 'false',
              })
            }
            color="success"
          />
        </FilterSection>
      </FilterPanel>

      {/* Desktop Table */}
      <DataTable
        columns={[
          {
            key: 'label',
            label: t('tire_hotel.label'),
            sortable: true,
            sortField: 'label',
            render: (item) => (
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {item.label || '-'}
              </Typography>
            ),
          },
          {
            key: 'building',
            label: t('tire_hotel.building'),
            sortable: true,
            sortField: 'building',
            render: (item) => item.building || '-',
          },
          {
            key: 'rack',
            label: t('tire_hotel.rack'),
            sortable: true,
            sortField: 'rack',
            render: (item) => item.rack || '-',
          },
          {
            key: 'shelf',
            label: t('tire_hotel.shelf'),
            render: (item) => item.shelf || '-',
          },
          {
            key: 'position',
            label: t('tire_hotel.position'),
            render: (item) => item.position || '-',
          },
          {
            key: 'size_category',
            label: t('tire_hotel.size_category'),
            sortable: true,
            sortField: 'size_category',
            render: (item) => (
              <Chip
                size="small"
                label={t(`tire_hotel.${item.size_category}`)}
                color={getSizeCategoryColor(item.size_category)}
                sx={{ fontSize: '11px' }}
              />
            ),
          },
          {
            key: 'status',
            label: t('tire_hotel.status'),
            sortable: true,
            sortField: 'is_occupied',
            align: 'center',
            render: (item) => (
              <Chip
                size="small"
                label={item.is_occupied ? t('tire_hotel.occupied') : t('tire_hotel.available')}
                color={item.is_occupied ? 'warning' : 'success'}
                variant={item.is_occupied ? 'filled' : 'outlined'}
                sx={{ fontSize: '11px' }}
              />
            ),
          },
          {
            key: 'customer',
            label: t('tire_hotel.customer'),
            maxWidth: 200,
            render: (item) => item.occupying_card_customer || '-',
          },
          {
            key: 'registration_number',
            label: t('tire_hotel.registration_number'),
            sortable: true,
            sortField: 'occupying_registration_number',
            width: '1%',
            render: (item) => item.occupying_card_registration_number || '-',
          },
          {
            key: 'actions',
            label: '',
            width: '1%',
            render: (item) => (
              <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton
                  size="small"
                  onClick={(e) => handleContextMenu(e, item)}
                  sx={{ color: 'text.disabled', '&:hover': { color: 'text.primary' } }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
            ),
          },
        ]}
        data={items}
        loading={loading}
        onRowClick={(item) => {
          setEditingLocation(item);
          setLocationDialogOpen(true);
        }}
        sortField={preferences.sortField}
        sortDirection={preferences.sortDirection}
        onSort={handleSort}
        emptyIcon={<WarehouseIcon />}
        emptyTitle={t('No items found')}
        testIdPrefix="storage-location-row"
        tableTestId="storage-locations-table"
        pagination={{
          page,
          totalPages,
          totalCount,
          pageSize,
          onPageChange: setPage,
          onPageSizeChange: setPageSize,
        }}
      />

      {/* Mobile Cards - Hidden on desktop */}
      <Box
        sx={{ display: { xs: 'block', lg: 'none' }, position: 'relative' }}
        data-testid="storage-locations-table-mobile"
      >
        {loading && items.length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              pt: 8,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}
        {loading && items.length === 0 ? (
          <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : items.length === 0 ? (
          <Paper elevation={0} sx={{ p: 4, textAlign: 'center' }}>
            <WarehouseIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary">
              {t('No items found')}
            </Typography>
          </Paper>
        ) : (
          items.map((item) => (
            <Paper
              key={item.id}
              elevation={0}
              data-testid={`storage-location-row-mobile-${item.id}`}
              onClick={() => {
                setEditingLocation(item);
                setLocationDialogOpen(true);
              }}
              sx={{
                p: 2,
                mb: 1.5,
                cursor: 'pointer',
                transition: 'background-color 0.15s ease',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', gap: 1.5, flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '10px',
                      bgcolor: item.is_occupied
                        ? 'rgba(237, 108, 2, 0.1)'
                        : 'rgba(46, 125, 50, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: item.is_occupied ? 'warning.main' : 'success.main',
                      flexShrink: 0,
                    }}
                  >
                    <WarehouseIcon />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: '15px',
                        fontWeight: 600,
                        color: 'text.primary',
                      }}
                    >
                      {item.label || `${item.building || ''}-${item.rack}-${item.shelf || ''}-${item.position || ''}`.replace(/^-+|-+$/g, '')}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '13px',
                        color: 'text.secondary',
                        mt: 0.25,
                      }}
                    >
                      {[item.building, item.rack, item.shelf, item.position].filter(Boolean).join(' / ')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Chip
                        size="small"
                        label={t(`tire_hotel.${item.size_category}`)}
                        color={getSizeCategoryColor(item.size_category)}
                        sx={{ fontSize: '11px' }}
                      />
                      <Chip
                        size="small"
                        label={item.is_occupied ? t('tire_hotel.occupied') : t('tire_hotel.available')}
                        color={item.is_occupied ? 'warning' : 'success'}
                        variant={item.is_occupied ? 'filled' : 'outlined'}
                        sx={{ fontSize: '11px' }}
                      />
                    </Box>
                    {item.occupying_card_customer && (
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: 'text.secondary',
                          mt: 0.5,
                        }}
                      >
                        {item.occupying_card_customer}
                        {item.occupying_card_registration_number && ` (${item.occupying_card_registration_number})`}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleContextMenu(e, item);
                  }}
                  sx={{
                    color: 'text.disabled',
                    '&:hover': { color: 'text.primary' },
                  }}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>
            </Paper>
          ))
        )}
      </Box>

      {/* Mobile Pagination */}
      <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
        <PaginationControls
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          loading={loading}
        />
      </Box>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem
          onClick={() => {
            handleCloseContextMenu();
            setEditingLocation(selectedItem);
            setLocationDialogOpen(true);
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('Edit')}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleCloseContextMenu();
            setDeleteConfirmOpen(true);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          <ListItemText>{t('tire_hotel.delete_location')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setSelectedItem(null);
        }}
      >
        <DialogTitle>{t('tire_hotel.delete_location')}</DialogTitle>
        <DialogContent>
          {selectedItem?.is_occupied ? (
            <>
              <Alert severity="warning" sx={{ mb: 1 }}>
                {t('tire_hotel.cannot_delete_occupied')}
              </Alert>
              {selectedItem.occupying_card_customer && (
                <Typography variant="body2" color="text.secondary">
                  {selectedItem.occupying_card_customer}
                </Typography>
              )}
            </>
          ) : (
            <Typography>{t('tire_hotel.confirm_delete_location')}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteConfirmOpen(false);
              setSelectedItem(null);
            }}
          >
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteLocation}
            disabled={!!selectedItem?.is_occupied}
          >
            {t('Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create/Edit Location Dialog */}
      <StorageLocationDialog
        open={locationDialogOpen}
        onClose={() => {
          setLocationDialogOpen(false);
          setEditingLocation(null);
        }}
        onSave={handleSaveLocation}
        location={editingLocation}
        t={t}
      />

      {/* Bulk Add Dialog */}
      <BulkAddLocationsDialog
        open={bulkAddDialogOpen}
        onClose={() => setBulkAddDialogOpen(false)}
        onSave={handleBulkAdd}
        t={t}
      />
    </Box>
  );
}

export default withTranslation()(StorageLocationsPage);
